import { useEffect, useRef, useCallback, useState } from 'react'
import { event } from 'lib/gtag'

interface CardScrollTrackingOptions {
  position: number // リスト内での位置
  threshold?: number
  rootMargin?: string
  eventName?: string
  category?: string
  label?: string
}

interface CardScrollTrackingResult {
  ref: React.RefObject<any>
  isVisible: boolean
  hasTracked: boolean
}

export const useCardScrollTracking = (
  options: CardScrollTrackingOptions
): CardScrollTrackingResult => {
  const {
    position,
    threshold = 0.5,
    rootMargin = '0px',
    eventName = 'card_scroll_tracking',
    category = 'engagement',
    label = 'recommended_space_card',
  } = options

  const ref = useRef<any>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [hasTracked, setHasTracked] = useState(false)

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !hasTracked) {
          setIsVisible(true)
          setHasTracked(true)

          // Google Analyticsにイベント送信
          window.dataLayer.push({
            event: eventName,
            event_category: category,
            event_label: label,
            value: position, // リスト内での位置を値として送信
            custom_event_type: 'scroll',
          })
        }
      })
    },
    [hasTracked, eventName, category, label, position]
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      threshold,
      rootMargin,
    })

    const currentRef = ref.current
    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [handleIntersection, threshold, rootMargin])

  return { ref, isVisible, hasTracked }
}
